import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Descriptions,
  Result,
  Spin,
  Alert,
  Typography,
  Table,
  Tag,
  Steps,
  Statistic,
  Row,
  Col,
  Progress,
} from "antd";
import {
  CheckCircleOutlined,
  FileOutlined,
  RocketOutlined,
  SettingOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { StepOneData } from "./StepOne";
import { StepTwoData } from "./StepTwo";
import { v4 as uuidv4 } from "uuid";
import axiosInstance from "../../../../utils/axiosInstance";
import { uploadFileToS3 } from "../../../../utils/s3Upload";
import { JobType } from "../../../../constants/JobTypes";
import { getTenantId } from "../../../../utils/auth";
import { useNavigate } from "react-router-dom";

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

interface StepThreeProps {
  stepOneData: StepOneData;
  stepTwoData: StepTwoData;
  onPrevious: () => void;
  onNext?: (jobConfigId: string) => void;
}

const StepThree: React.FC<StepThreeProps> = ({
  stepOneData,
  stepTwoData,
  onPrevious,
  onNext,
}) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [jobId, setJobId] = useState<string>("");
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(45);
  const [processingProgress, setProcessingProgress] = useState(0);

  // Countdown effect
  useEffect(() => {
    if (showCountdown && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
        setProcessingProgress(Math.floor(((45 - countdown + 1) / 45) * 100));
      }, 1000);

      return () => clearTimeout(timer);
    } else if (showCountdown && countdown === 0) {
      // When countdown reaches zero, proceed to results
      if (onNext && jobId) {
        onNext(jobId);
      }
    }
  }, [showCountdown, countdown, jobId, onNext]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    setCurrentStep(0);

    try {
      // Generate a job ID
      const newJobId = uuidv4();
      setJobId(newJobId);

      // Step 1: Upload file
      setCurrentStep(1);
      const mmmFilePath = newJobId + "/input/bid_multiplier_data.csv";
      await uploadFileToS3(stepOneData.dataFile, mmmFilePath);

      // Step 2: Create job configuration
      setCurrentStep(2);
      const payload = {
        jobConfigId: newJobId,
        name: stepTwoData.name,
        jobType: JobType.BidOptimizer,
        tenantId: getTenantId(),
        dataSourceConfig: {
          dataSourceType: "FILE_CSV",
        },
        dataTransformationConfig: {},
        dataMappingConfig: {
          mappings: {
            bidMultiplier: {
              columnMapping: {
                age_varialbes: !stepTwoData.age ? undefined : [stepTwoData.age],
                device_platform_varialbes: !stepTwoData.devicePlatform
                  ? undefined
                  : [stepTwoData.devicePlatform],
                gender_varialbes: !stepTwoData.gender
                  ? undefined
                  : [stepTwoData.gender],
                locale_varialbes: !stepTwoData.locale
                  ? undefined
                  : [stepTwoData.locale],
                region_varialbes: !stepTwoData.geoLocation
                  ? undefined
                  : [stepTwoData.geoLocation],
                user_devices_varialbes: !stepTwoData.userDevice
                  ? undefined
                  : [stepTwoData.userDevice],
                user_os_varialbes: !stepTwoData.userOs
                  ? undefined
                  : [stepTwoData.userOs],
                target_variables: !stepTwoData.targetVariable
                  ? undefined
                  : [stepTwoData.targetVariable],
              },
            },
          },
        },
        modelVersion: "V1",
        modelParameterConfig: {
          parameters: {
            campaignObjective: stepTwoData.campaignObjective,
            geoLocationType: stepTwoData.geoLocationType,
          },
        },
        modelOutputConfig: {},
      };

      // Send the POST request
      await axiosInstance.post("/api/jobConfig", payload);
      await axiosInstance.post("/api/jobRun", {
        jobConfigId: newJobId,
      });

      // Step 3: Complete 
      setCurrentStep(3);
      setIsSuccess(true);
      setIsSubmitting(false);

      // Start the countdown if onNext is provided
      if (onNext) {
        // Start the countdown
        setShowCountdown(true);

        // Also start the job run
        await axiosInstance.post("/api/jobRun", {
          jobConfigId: newJobId,
        });
      } else {
        // Otherwise, navigate back after 3 seconds (fallback behavior)
        setTimeout(() => {
          navigate(-1);
        }, 3000);
      }
    } catch (error) {
      console.error("Error:", error);
      setError("An error occurred while creating the job. Please try again.");
      setIsSubmitting(false);
    } 
  };

  // Format the selected variables for display
  const getVariablesList = () => {
    const variables = [];

    if (stepTwoData.userId) {
      variables.push({
        key: "userId",
        name: "Customer Identifier",
        value: stepTwoData.userId,
        category: "Required",
        type: "success", 
      });
    }

    if (stepTwoData.targetVariable) {
      variables.push({
        key: "targetVariable",
        name: "Target Variable",
        value: stepTwoData.targetVariable,
        category: "Required",
        type: "success",
      });
    }

    if (stepTwoData.age) {
      variables.push({
        key: "age", 
        name: "Age Range",
        value: stepTwoData.age,
        category: "Demographic",
        type: "processing",
      });
    }

    if (stepTwoData.gender) {
      variables.push({
        key: "gender",
        name: "Gender",
        value: stepTwoData.gender,
        category: "Demographic",
        type: "processing",
      }); 
    }

    if (stepTwoData.locale) {
      variables.push({
        key: "locale",
        name: "Locale",
        value: stepTwoData.locale,
        category: "Demographic",
        type: "processing",
      });
    }

    if (stepTwoData.geoLocationType) {
      variables.push({ 
        key: "geoLocationType",
        name: "Geo Location Type",
        value:
          stepTwoData.geoLocationType === "dma"
            ? "Designated Market Area"
            : "Regions",
        category: "Geographic",
        type: "processing",
      });
    }

    if (stepTwoData.geoLocation) {
      variables.push({ 
        key: "geoLocation",
        name: "Geo Location",
        value: stepTwoData.geoLocation,
        category: "Geographic",
        type: "processing",
      });
    }

    if (stepTwoData.devicePlatform) {
      variables.push({
        key: "devicePlatform",
        name: "Device Platform", 
        value: stepTwoData.devicePlatform,
        category: "Device",
        type: "default",
      });
    }

    if (stepTwoData.userOs) {
      variables.push({
        key: "userOs",
        name: "User OS",
        value: stepTwoData.userOs,
        category: "Device",
        type: "default",
      });
    } 

    if (stepTwoData.userDevice) {
      variables.push({
        key: "userDevice",
        name: "User Device",
        value: stepTwoData.userDevice,
        category: "Device",
        type: "default",
      });
    }
 
    return variables;
  };

  const columns = [
    {
      title: "Variable",
      dataIndex: "name",
      key: "name", 
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: "Selected Column",
      dataIndex: "value",
      key: "value",
    },
    {
      title: "Category",
      dataIndex: "category", 
      key: "category",
      render: (category: string) => {
        let color = "blue";
        if (category === "Demographic") color = "green";
        if (category === "Geographic") color = "cyan";
        if (category === "Device") color = "orange";

        return <Tag color={color}>{category}</Tag>;
      },
    },
  ];

  if (isSuccess) {
    return (
      <Result
        status="success" 
        title="Bid Optimizer Job Created Successfully!"
        subTitle={
          <div>
            <p>
              Your bid optimizer job has been created and is now processing.
            </p>
            <p>Job ID: {jobId}</p>

            {showCountdown && (
              <div className="mt-6 mb-4">
                <Card className="bg-blue-50 border-blue-200">
                  <div className="flex items-center mb-4">
                    <ClockCircleOutlined className="text-blue-500 text-xl mr-3" />
                    <Title level={5} className="m-0">
                      Processing Job Data 
                    </Title>
                  </div>

                  <div className="text-center mb-4">
                    <Text>
                      Please wait while we process your data. This may take up
                      to 45 seconds.
                    </Text>
                  </div>

                  <Progress
                    percent={processingProgress}
                    status="active"
                    strokeColor={{
                      "0%": "#108ee9", 
                      "100%": "#87d068",
                    }}
                  />

                  <div className="text-center mt-4">
                    <Text strong>Time remaining: {countdown} seconds</Text>
                  </div>

                  <div className="text-center mt-4">
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 24 }} spin />
                      }
                    /> 
                  </div>
                </Card>
              </div>
            )}
          </div>
        }
        icon={<CheckCircleOutlined className="text-green-500 text-6xl" />}
        extra={[
          onNext && !showCountdown ? (
            <Button
              type="primary"
              key="next"
              onClick={() => onNext(jobId)} 
              size="large"
            >
              View Results
            </Button>
          ) : !showCountdown ? (
            <Button
              type="primary" 
              key="console"
              onClick={() => navigate(-1)}
              size="large"
            >
              Go Back to Jobs
            </Button>
          ) : (
            <Button
              type="default" 
              key="skip"
              onClick={() => onNext && onNext(jobId)}
              size="large"
            >
              Skip Waiting
            </Button>
          ),
        ]}
      />
    );
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-sm">
        <Title level={4}>Review and Submit</Title>
        <Paragraph className="text-gray-600 mb-6">
          Review your configuration before submitting the job. Make sure all the
          selected variables are correct.
        </Paragraph>
 
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error" 
            showIcon
            icon={<ExclamationCircleOutlined />}
            className="mb-6"
          />
        )}

        {isSubmitting && ( 
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <Steps current={currentStep}>
              <Step
                title="Preparing"
                icon={currentStep === 0 ? <LoadingOutlined /> : null}
              />
              <Step
                title="Uploading File"
                icon={currentStep === 1 ? <LoadingOutlined /> : null}
              />
              <Step
                title="Creating Job" 
                icon={currentStep === 2 ? <LoadingOutlined /> : null}
              />
              <Step
                title="Completed"
                icon={currentStep === 3 ? <LoadingOutlined /> : null}
              />
            </Steps>
            <div className="text-center mt-6">
              <Spin
                tip={
                  currentStep === 0
                    ? "Preparing job..."
                    : currentStep === 1
                    ? "Uploading file..." 
                    : currentStep === 2
                    ? "Creating job configuration..."
                    : "Finalizing..."
                }
              />
            </div>
          </Card> 
        )}

        <Row gutter={24} className="mb-6">
          <Col span={8}>
            <Card bordered={false} className="h-full bg-gray-50">
              <Statistic
                title="Data File" 
                value={stepOneData.dataFile.name}
                prefix={<FileOutlined />}
                valueStyle={{ fontSize: "16px" }}
              />
              <Text type="secondary" className="block mt-2">
                Size: {(stepOneData.dataFile.size / 1024).toFixed(2)} KB
              </Text>
            </Card>
          </Col> 
          <Col span={8}>
            <Card bordered={false} className="h-full bg-gray-50">
              <Statistic
                title="Job Name"
                value={stepTwoData.name}
                prefix={<RocketOutlined />}
                valueStyle={{ fontSize: "16px" }}
              />
              <Text type="secondary" className="block mt-2">
                Campaign Objective:{" "}
                {stepTwoData.campaignObjective === "lead"
                  ? "Lead Generation"
                  : stepTwoData.campaignObjective === "sales"
                  ? "Sales"
                  : "Reach"}
              </Text>
            </Card>
          </Col>
          <Col span={8}>
            <Card bordered={false} className="h-full bg-gray-50">
              <Statistic
                title="Variables Selected"
                value={getVariablesList().length}
                prefix={<SettingOutlined />}
                valueStyle={{ fontSize: "16px" }}
              />
              <Text type="secondary" className="block mt-2">
                {
                  getVariablesList().filter((v) => v.category === "Required")
                    .length
                }{" "}
                required,
                {
                  getVariablesList().filter((v) => v.category !== "Required")
                    .length
                }{" "}
                optional
              </Text>
            </Card>
          </Col>
        </Row>

        <Card
          title={<Title level={5}>Selected Variables</Title>}
          className="mb-6"
          bordered={false}
        >
          <Table
            dataSource={getVariablesList()}
            columns={columns}
            pagination={false}
            rowKey="key"
            size="middle"
          />
        </Card>

        <Card
          title={<Title level={5}>Data Preview</Title>}
          className="mb-6"
          bordered={false}
        >
          <div className="max-h-[200px] overflow-auto">
            <Table
              dataSource={stepOneData.previewData.slice(0, 5)}
              columns={stepOneData.headers.map((header) => ({
                title: header,
                dataIndex: header,
                key: header,
                ellipsis: true,
              }))}
              size="small"
              pagination={false}
              bordered
              scroll={{ x: "max-content" }}
              rowKey={(record, index) => index?.toString() || "0"}
            />
          </div>
          <div className="text-right mt-2">
            <Text type="secondary">Showing first 5 rows of data</Text>
          </div>
        </Card>

        <div className="flex justify-between">
          <Button size="large" onClick={onPrevious} disabled={isSubmitting}>
            Previous
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            loading={isSubmitting}
            icon={<RocketOutlined />}
          >
            Create Bid Optimizer Job
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default StepThree;
